import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Loader2, Building2, TrendingUp, Users, DollarSign, Sparkles,
  ArrowRight, Ticket, Gift, Calendar, BarChart3, Shield,
  Zap, Globe, CheckCircle, Clock, CreditCard, Mail
} from 'lucide-react';

const PartnerAuth = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('register');
  const [stats, setStats] = useState(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '', password: '', full_name: ''
  });

  useEffect(() => {
    api.get('/platform/stats').then(res => setStats(res.data)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      if (response.user?.role === 'admin') {
        navigate('/admin');
      } else if (response.user?.role === 'partner') {
        navigate(response.user?.onboarding_complete ? '/partner/dashboard' : '/partner/onboarding');
      } else {
        toast.error('This login is for partners only');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.full_name, 'partner');
      toast.success('Account created! Let\'s set up your venue.');
      navigate('/partner/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const formatStat = (value, prefix = '') => {
    if (value === null || value === undefined) return '—';
    if (value >= 1000000) return `${prefix}${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${prefix}${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}K`;
    return `${prefix}${value}`;
  };

  return (
    <div className="min-h-screen bg-[#0f172a]">
      {/* Header */}
      <header className="border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl font-bold text-white tracking-wider">EVENT<span className="text-amber-400">★</span>GOERS</span>
            <Badge className="bg-indigo-600 text-white border-0 text-[10px]">PARTNERS</Badge>
          </Link>
          <Link to="/auth" className="text-slate-400 hover:text-white text-sm transition-colors">
            Customer Login
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-600/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Value Proposition */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Badge className="mb-4 bg-indigo-600/20 text-indigo-300 border-indigo-500/30">No upfront costs</Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Turn Your Venue Into Denver's Next{' '}
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Must-Visit Destination</span>
              </h1>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                EVENT★GOERS connects your venue with thousands of experience seekers across Denver's hottest neighborhoods. Sell tickets, offer experiences, take reservations — all from one platform.
              </p>

              {/* What You Get */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {[
                  { icon: Ticket, title: 'Event Ticketing', desc: 'Sell tickets with built-in checkout' },
                  { icon: Gift, title: 'Experience Vouchers', desc: 'Create bookable experiences' },
                  { icon: Calendar, title: 'Reservations', desc: 'Table bookings with card holds' },
                  { icon: Sparkles, title: 'AI Marketing', desc: 'Generate content in one click' },
                  { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Real-time performance data' },
                  { icon: Mail, title: 'Customer Engagement', desc: 'Automated emails & reminders' },
                ].map((item, i) => (
                  <motion.div key={item.title} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}
                    className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/40 border border-slate-700/50">
                    <div className="w-9 h-9 rounded-lg bg-indigo-600/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4.5 h-4.5 text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{item.title}</p>
                      <p className="text-slate-500 text-xs">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Live Platform Stats */}
              <div className="border-t border-slate-800 pt-8">
                <p className="text-slate-500 text-sm mb-5 uppercase tracking-wider font-medium">Platform at a glance</p>
                <div className="grid grid-cols-5 gap-4" data-testid="platform-stats">
                  {[
                    { label: 'Partner Venues', value: formatStat(stats?.partner_venues), testId: 'stat-venues' },
                    { label: 'Active Users', value: formatStat(stats?.active_users), testId: 'stat-users' },
                    { label: 'Partner Revenue', value: formatStat(stats?.total_revenue, '$'), testId: 'stat-revenue' },
                    { label: 'Events', value: formatStat(stats?.total_events), testId: 'stat-events' },
                    { label: 'Experiences', value: formatStat(stats?.total_experiences), testId: 'stat-experiences' },
                  ].map(s => (
                    <div key={s.label} className="text-center" data-testid={s.testId}>
                      <p className="text-2xl font-bold text-white">{s.value}</p>
                      <p className="text-[11px] text-slate-500">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right - Auth Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-slate-900/80 border-slate-700/50 backdrop-blur-sm shadow-2xl" data-testid="partner-auth-card">
                <CardHeader className="text-center pb-2">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-white">
                    {activeTab === 'register' ? 'Start Growing Your Venue' : 'Welcome Back, Partner'}
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    {activeTab === 'register' ? 'Create your free partner account in 30 seconds' : 'Sign in to your partner dashboard'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-slate-800">
                      <TabsTrigger value="register" data-testid="partner-register-tab" className="data-[state=active]:bg-indigo-600">Get Started</TabsTrigger>
                      <TabsTrigger value="login" data-testid="partner-login-tab" className="data-[state=active]:bg-indigo-600">Sign In</TabsTrigger>
                    </TabsList>

                    <TabsContent value="register" className="mt-6">
                      <form onSubmit={handleRegister} className="space-y-4">
                        <div className="space-y-1.5">
                          <Label className="text-slate-300">Your Name</Label>
                          <Input placeholder="Jane Smith" value={registerData.full_name} onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })} required
                            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="partner-register-name" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-slate-300">Business Email</Label>
                          <Input type="email" placeholder="you@yourvenue.com" value={registerData.email} onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })} required
                            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="partner-register-email" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-slate-300">Password</Label>
                          <Input type="password" placeholder="Min 8 characters" value={registerData.password} onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })} required minLength={8}
                            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="partner-register-password" />
                        </div>
                        <Button type="submit" className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold" disabled={loading} data-testid="partner-register-submit">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Partner Account <ArrowRight className="w-4 h-4 ml-2" /></>}
                        </Button>
                        <p className="text-[11px] text-slate-500 text-center">
                          Free to join. No credit card required. By signing up you agree to our Terms & Partner Agreement.
                        </p>
                      </form>
                    </TabsContent>

                    <TabsContent value="login" className="mt-6">
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-1.5">
                          <Label className="text-slate-300">Email</Label>
                          <Input type="email" placeholder="you@yourvenue.com" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required
                            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="partner-login-email" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-slate-300">Password</Label>
                          <Input type="password" placeholder="••••••••" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required
                            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="partner-login-password" />
                        </div>
                        <Button type="submit" className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold" disabled={loading} data-testid="partner-login-submit">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In to Partner Portal'}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>

                  {/* What happens next */}
                  <div className="mt-8 pt-6 border-t border-slate-800">
                    <p className="text-sm text-slate-400 mb-4 text-center font-medium">How it works</p>
                    <div className="space-y-3">
                      {[
                        { num: '1', text: 'Create account & build your profile (5 min)', icon: Zap },
                        { num: '2', text: 'Quick admin review & approval', icon: Shield },
                        { num: '3', text: 'Go live — start selling & earning', icon: DollarSign },
                      ].map(step => (
                        <div key={step.num} className="flex items-center gap-3 text-sm">
                          <div className="w-7 h-7 rounded-full bg-indigo-600/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs text-indigo-400 font-bold">{step.num}</span>
                          </div>
                          <span className="text-slate-400">{step.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trust badges */}
                  <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-slate-600">
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> SSL Secured</span>
                    <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> Stripe Payments</span>
                    <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> Denver, CO</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Commission Structure */}
      <section className="border-t border-slate-800/50 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white mb-2">Transparent Pricing</h2>
            <p className="text-slate-400">No monthly fees. You only pay when you earn.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { title: 'Event Tickets', rate: '12%', desc: 'Per ticket sold. Service fee charged to customer.', icon: Ticket },
              { title: 'Experiences', rate: '25%', desc: 'Commission on experience voucher sales.', icon: Gift },
              { title: 'Private Hire', rate: '15%', desc: 'Commission on private event bookings.', icon: Building2 },
            ].map(item => (
              <Card key={item.title} className="bg-slate-800/50 border-slate-700/50 text-center">
                <CardContent className="pt-6">
                  <item.icon className="w-8 h-8 text-indigo-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">{item.rate}</p>
                  <p className="text-white font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-slate-500 text-xs">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnerAuth;
